import { useState, useEffect, useRef, useMemo } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, ArrowRight, X, MapPin, Eye } from 'lucide-react';
import { useMapOverlayPosition } from '../../hooks/useMapOverlayPosition';
import { useHighlightLayer } from '../../hooks/useHighlightLayer';
import { useMap } from '@/providers/MapProvider';
import { useSelectionStore, SelectedFeature } from '@/stores/selectionStore';
import { FieldType, SystemLayerKey } from '@packages/database/types';
import { hexToRgba } from '@/utils/map.utils';
import {
  getPrimaryLabelField,
  getPopupFields,
} from '@/helpers/layerSchema.helpers';
import { useDialogManager } from '@/hooks/useDialogManager';
import { useSearchParams } from 'react-router';
import {
  FEATURE_REGISTRY,
  getFeatureRegistry,
} from '../../config/featureRegistry';

function getDisplayInfo(item: SelectedFeature) {
  const { layer, data } = item;
  if (!layer) return null;

  const labelField = getPrimaryLabelField(layer.schemaConfig);
  const title = String(
    data?.[labelField?.alias ?? 'name'] ?? item.id ?? 'Unknown',
  );

  return {
    title: layer.displayName,
    subtitle: title,
    color: layer.styles?.color ?? '#94a3b8',
  };
}

interface FeatureInfoPopupProps {
  items: SelectedFeature[];
  coordinate: number[] | null;
}

export const FeatureInfoPopup = () => {
  const { selected, activeIndex, next, prev, coordinate, clear, setSelection } =
    useSelectionStore();

  const isMulti = selected.length > 1;
  const currentItem = selected[activeIndex];
  const featureRegistry = getFeatureRegistry(currentItem?.layerKey);

  const containerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const info = useMemo(
    () => (currentItem ? getDisplayInfo(currentItem) : null),
    [currentItem],
  );

  const isAsset = currentItem?.layer?.sourceTable === 'assets';
  const isTicket = currentItem?.layerKey === SystemLayerKey.TICKET;
  const canView = isAsset || isTicket;

  let showScroller = isMulti || featureRegistry?.presentation === 'card';

  const handleViewDetails = () => {
    setSelection([currentItem], 'scroller');
  };

  useMapOverlayPosition({
    position: coordinate,
    elementRef: containerRef,
    isOpen: showScroller,
    offset: [0, -12],
  });

  if (!showScroller) return null;

  return (
    <div
      ref={containerRef}
      className="absolute top-0 left-0 z-[60]"
      style={{ display: 'none' }}
    >
      <Card className="w-72 py-2 shadow-xl border-border overflow-hidden ring-1 ring-ring-secondary">
        {/* Header */}
        <div className="flex items-center justify-between px-3 py-1.5 bg-card border-b border-border">
          <Badge
            variant="secondary"
            className="bg-background border text-xs font-medium text-foreground"
          >
            {activeIndex + 1} / {selected.length}
          </Badge>
          <div className="flex items-center gap-1">
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 text-muted-foreground"
              onClick={prev}
            >
              <ArrowLeft size={14} />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 text-muted-foreground"
              onClick={next}
            >
              <ArrowRight size={14} />
            </Button>
            <div className="w-px h-4 bg-muted-foreground mx-1" />
            <Button
              variant="ghost"
              size="icon"
              className="h-4 w-4 text-muted-foreground"
              onClick={clear}
            >
              <X size={14} />
            </Button>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 py-2 bg-card">
          <div className="flex gap-3 mb-3 items-center">
            <div
              className="w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border border-border"
              style={{
                backgroundColor: hexToRgba(info?.color ?? '#000000', 0.1),
                color: info?.color,
              }}
            >
              <MapPin size={20} />
            </div>
            <div className="min-w-0">
              <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">
                {info?.subtitle}
              </div>
              <div
                className="font-bold text-foreground text-sm truncate"
                title={info?.title}
              >
                {info?.title}
              </div>
            </div>
          </div>

          <FeatureAttributes item={currentItem} />

          {/* View Details — assets only */}
          {canView && (
            <Button
              size="sm"
              className="w-full text-xs h-8 mt-3 text-white"
              onClick={handleViewDetails}
            >
              <Eye className="w-3.5 h-3.5 mr-2" />
              View Details
            </Button>
          )}
        </div>
      </Card>

      <div className="absolute left-1/2 bottom-0 translate-y-1/2 -translate-x-1/2 w-3 h-3 bg-card border-r border-b border-border rotate-45" />
    </div>
  );
};

// =========================================================================
// FEATURE ATTRIBUTES
// =========================================================================

const FeatureAttributes = ({ item }: { item: SelectedFeature }) => {
  if (!item?.layer) return null;

  const visibleFields = getPopupFields(item.layer.schemaConfig);

  if (!visibleFields.length) return null;

  return (
    <div className="space-y-1 border-t border-border pt-2 mt-1">
      {visibleFields.map((field) => {
        const value = item.data?.[field.alias];
        if (value == null || value === '') return null;

        return (
          <div
            key={field.column}
            className="flex justify-between text-xs gap-2"
          >
            <span className="text-muted-foreground shrink-0">
              {field.fieldLabel}
            </span>
            <span className="text-foreground font-medium truncate text-right">
              {formatFieldValue(field.fieldType!, value)}
            </span>
          </div>
        );
      })}
    </div>
  );
};

function formatFieldValue(fieldType: FieldType, value: unknown): string {
  if (value == null) return '—';
  switch (fieldType) {
    case FieldType.DATE:
      return new Date(String(value)).toLocaleDateString();
    case FieldType.BOOLEAN:
      return value ? 'Yes' : 'No';
    case FieldType.NUMBER:
      return typeof value === 'number' ? value.toLocaleString() : String(value);
    default:
      return String(value);
  }
}
